import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useBranch, type Role } from "@/context/BranchContext";
import { landingForPermissions } from "@/config/permissions";
import { mockBranchEmployees, defaultRole } from "@/mocks/branch/employee";
import { SHOW_DESIGN_PREVIEW } from "@/config/previewMode";
import Button from "@/components/base/Button";
import OtpInput from "./components/OtpInput";
import BlockedNotice, { type BlockedType } from "./components/BlockedNotice";
import RoleSelect from "./components/RoleSelect";

const LOGO_URL =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/70345e36bb0ad42c4823216a62f4fb9a.png";
const ICON_URL =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/8a7ae8c47f80a7ae4dacefc8dacd2c1f.png";

type Step = "phone" | "otp" | "blocked";

const DEMO_OTP = "1234";
const SAUDI_PHONE = /^05\d{8}$/;

const BLOCKED_PHONES: Record<string, BlockedType> = {
  "0500000001": "cancelled",
  "0500000002": "branch_disabled",
  "0500000003": "store_suspended",
  "0599999999": "unregistered",
};

function toLocalPhone(input: string) {
  const raw = input.replace(/\D/g, "");
  const national = raw.startsWith("0") ? raw.slice(1) : raw;
  return `0${national}`;
}

export default function BranchLogin() {
  const navigate = useNavigate();
  const { login, enterPreview } = useBranch();

  const [step, setStep] = useState<Step>("phone");
  const [previewRole, setPreviewRole] = useState<Role>("cashier");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [blockedType, setBlockedType] = useState<BlockedType>("cancelled");
  const [seconds, setSeconds] = useState(60);

  const localPhone = toLocalPhone(phone);
  const maskedPhone = localPhone.replace(/(\d{4})\d{3}(\d{3})/, "$1***$2");

  useEffect(() => {
    if (step !== "otp") return;
    setSeconds(60);
    setOtp("");
    setOtpError("");
  }, [step]);

  useEffect(() => {
    if (step !== "otp" || seconds <= 0) return;
    const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, seconds]);

  const handlePhoneSubmit = (event: FormEvent) => {
    event.preventDefault();
    const local = toLocalPhone(phone);
    if (!SAUDI_PHONE.test(local)) {
      setPhoneError("رقم الجوال غير صحيح — أدخل رقم سعودي من 9 أرقام يبدأ بـ 5");
      return;
    }
    setPhoneError("");
    const blocked = BLOCKED_PHONES[local];
    if (blocked) {
      setBlockedType(blocked);
      setStep("blocked");
      return;
    }
    setStep("otp");
  };

  const submitOtp = (code: string) => {
    if (code.length < 4) {
      setOtpError("أدخل رمز التحقق المكوّن من 4 أرقام");
      return;
    }
    if (seconds <= 0) {
      setOtpError("انتهت صلاحية الرمز — أعد إرسال الرمز من جديد");
      return;
    }
    if (code !== DEMO_OTP) {
      setOtpError("رمز التحقق غير صحيح — استخدم 1234 في الوضع التجريبي");
      return;
    }
    setOtpError("");
    setTimeout(() => {
      // الدخول الحقيقي: النظام يسترجع الدور والصلاحيات المحفوظة من رقم الجوال المسجّل لدى صاحب المحل
      const employee = login(localPhone);
      navigate(landingForPermissions(employee.permissions), { replace: true });
    }, 600);
  };

  // معاينة تصميم فقط: تفتح اللوحة مباشرة وفق النموذج المحفوظ لهذا المسمى (الصلاحيات محددة مسبقًا من لوحة المحل التجاري)
  const handlePreview = () => {
    const saved = mockBranchEmployees[previewRole] ?? mockBranchEmployees[defaultRole];
    enterPreview(previewRole, { ...saved.permissions });
    navigate(landingForPermissions(saved.permissions), { replace: true });
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col bg-background-100">
      <div className="relative flex-1 w-full overflow-hidden flex items-center justify-center px-4 py-10">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-48 -right-32 w-[520px] h-[520px] rounded-full bg-primary-100/70 blur-3xl"></div>
          <div className="absolute -bottom-48 -left-32 w-[520px] h-[520px] rounded-full bg-accent-100/70 blur-3xl"></div>
          <img
            src={ICON_URL}
            alt=""
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[680px] max-w-none opacity-[0.035] select-none"
          />
        </div>

        <div className="relative w-full max-w-[460px]">
          <div className="bg-background-50 rounded-modal border border-background-200/70 p-7 sm:p-9 animate-scale-in">
            <div className="flex justify-center mb-8">
              <img src={LOGO_URL} alt="Your Cashier" className="h-16 sm:h-[72px] w-auto object-contain" />
            </div>

            {step === "phone" && (
              <div className="animate-fade-slide-up">
                <h1 className="text-center text-2xl font-bold text-foreground-900">
                  تسجيل الدخول إلى لوحة التحكم
                </h1>
                <p className="mt-2.5 text-center text-sm text-foreground-500 leading-relaxed">
                  أدخل رقم الجوال المرتبط بحسابك للمتابعة
                </p>

                <form onSubmit={handlePhoneSubmit} className="mt-8 space-y-4">
                  {SHOW_DESIGN_PREVIEW ? (
                    <RoleSelect
                      role={previewRole}
                      onRoleChange={setPreviewRole}
                      onPreview={handlePreview}
                    />
                  ) : null}

                  <div>
                    <label htmlFor="phone" className="block mb-2 text-sm font-semibold text-foreground-700">
                      رقم الجوال
                    </label>
                    <div
                      dir="ltr"
                      className={`flex items-stretch h-12 rounded-btn border bg-background-50 overflow-hidden transition-all ${
                        phoneError
                          ? "border-red-300 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100"
                          : "border-background-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100"
                      }`}
                    >
                      <span className="flex items-center gap-1.5 px-3.5 bg-background-100 border-r border-background-200 text-sm font-semibold text-foreground-600 select-none">
                        <i className="ri-flag-line text-base text-foreground-400"></i>
                        +966
                      </span>
                      <input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="5xxxxxxxx"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value.replace(/[^\d]/g, ""));
                          if (phoneError) setPhoneError("");
                        }}
                        autoComplete="tel"
                        className="flex-1 min-w-0 h-full px-3.5 bg-transparent text-foreground-900 placeholder:text-foreground-400 text-sm outline-none"
                      />
                    </div>
                    {phoneError ? (
                      <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                        <i className="ri-error-warning-line text-sm"></i>
                        {phoneError}
                      </p>
                    ) : null}
                  </div>

                  <Button type="submit" size="lg" fullWidth icon="ri-shield-keyhole-line">
                    إرسال رمز التحقق
                  </Button>
                </form>

                <p className="mt-4 text-center text-xs text-foreground-500 leading-relaxed">
                  سيتم تحديد دورك وصلاحياتك تلقائيًا بعد التحقق من رقم الجوال.
                </p>

                <div className="mt-6 pt-5 border-t border-background-100 text-center">
                  <a
                    href="mailto:support@yourcashier.sa"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-customer-service-2-line text-base"></i>
                    هل تواجه مشكلة في تسجيل الدخول؟ تواصل مع الدعم
                  </a>
                </div>
              </div>
            )}

            {step === "otp" && (
              <div className="animate-fade-slide-up">
                <h1 className="text-center text-2xl font-bold text-foreground-900">رمز التحقق</h1>
                <p className="mt-2.5 text-center text-sm text-foreground-500 leading-relaxed">
                  أرسلنا رمزاً مكوّناً من 4 أرقام إلى الرقم{" "}
                  <span className="font-semibold text-foreground-700" dir="ltr">
                    {maskedPhone}
                  </span>
                </p>

                <div className="mt-8">
                  <OtpInput
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      if (otpError) setOtpError("");
                    }}
                    onComplete={submitOtp}
                    error={Boolean(otpError)}
                  />
                  {otpError ? (
                    <p className="mt-3 text-center text-xs text-red-600">{otpError}</p>
                  ) : (
                    <p className="mt-3 text-center text-xs text-foreground-500">
                      الوضع التجريبي — استخدم الرمز <strong dir="ltr">1234</strong>
                    </p>
                  )}
                </div>

                <div className="mt-6 text-center text-sm">
                  {seconds > 0 ? (
                    <span className="text-foreground-500">
                      إعادة الإرسال بعد{" "}
                      <span dir="ltr" className="font-semibold text-foreground-700">
                        00:{String(seconds).padStart(2, "0")}
                      </span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setSeconds(60);
                        setOtp("");
                        setOtpError("");
                      }}
                      className="font-semibold text-primary-700 hover:underline cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <i className="ri-refresh-line"></i>
                      إعادة إرسال الرمز
                    </button>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("phone");
                      setOtp("");
                      setOtpError("");
                    }}
                    className="text-sm font-semibold text-foreground-500 hover:text-foreground-700 flex items-center gap-1.5 cursor-pointer"
                  >
                    <i className="ri-arrow-right-line"></i>
                    تعديل رقم الجوال
                  </button>
                </div>
              </div>
            )}

            {step === "blocked" && (
              <BlockedNotice
                type={blockedType}
                onBack={() => {
                  setStep("phone");
                  setPhone("");
                }}
              />
            )}
          </div>

          <p className="mt-6 text-center text-xs text-foreground-500">
            © 2026 Your Cashier — جميع الحقوق محفوظة
          </p>
        </div>
      </div>
    </div>
  );
}